/**
 * Baris editor pesanan admin (Detail PO: "Edit Pesanan" & "Pesanan Manual";
 * keranjang admin: "Edit Variasi"). Editor Edit Pesanan PEMBELI memakai aturan
 * yang sama lewat `orderEdit.ts`.
 *
 * ── SATU BARIS = SATU (VARIAN + CATATAN) ────────────────────────────────────
 * `order_items` memang menyimpan beberapa baris untuk varian yang sama dengan
 * catatan berbeda — itu bentuk normal dari checkout pembeli (tiap baris
 * keranjang punya catatannya sendiri) dan dipakai admin untuk menandai nama
 * penerima per pcs ("Supi", "Rina", …). RPC `admin_edit_closed_po_order` dan
 * `admin_create_*` juga menyisipkan SATU baris per elemen `p_lines`, tanpa
 * dedup.
 *
 * Yang dulu meruntuhkannya adalah editor sisi klien: ia mengunci identitas
 * baris pada NAMA VARIAN saja, sehingga dua baris "Merah / L" bercatatan beda
 * digabung jadi satu — lalu Simpan menulis ulang hasil gabungan itu dan
 * catatan yang kalah HILANG PERMANEN (RPC menghapus seluruh baris editable
 * sebelum menulis ulang). Identitas baris di sini karena itu varian + catatan.
 */

export type EditorLine = {
  /**
   * Kunci React yang stabil sepanjang umur baris. WAJIB tersendiri: dua baris
   * bisa punya `variantId`/`variantName` yang sama persis, dan key React yang
   * kembar membuat React menukar isi baris saat salah satunya dihapus.
   */
  uid: string;
  variantId: string | null;
  variantName: string;
  qty: number;
  note: string;
};

let uidSeq = 0;

/** Kunci baris baru — cukup unik per sesi editor, tak pernah disimpan ke DB. */
export function newLineUid(): string {
  uidSeq += 1;
  return `line-${uidSeq}`;
}

/**
 * Bentuk catatan yang dipakai untuk MEMBANDINGKAN dua baris.
 *
 * Sejalan dengan sisi DB: RPC menyimpan `nullif(left(trim(note), 20), '')`,
 * jadi "  Supi " dan "Supi" adalah catatan yang sama, dan "" sama dengan
 * tak-bercatatan. Tanpa penyeragaman ini admin bisa membuat dua baris yang
 * tampak identik lalu bingung kenapa keduanya ada.
 */
export function normalizeNote(note: string | null | undefined): string {
  return (note ?? "").trim();
}

/** Identitas baris: varian + catatan. Dipakai sebagai kunci penggabungan. */
export function lineIdentity(
  variantName: string,
  note: string | null | undefined
): string {
  // NUL tak mungkin muncul di nama varian maupun catatan, jadi
  // ("A", "B|C") tak pernah bertabrakan dengan ("A|B", "C").
  return `${variantName}\u0000${normalizeNote(note)}`;
}

export function makeEditorLine(input: {
  variantId: string | null;
  variantName: string;
  qty: number;
  note?: string | null;
}): EditorLine {
  return {
    uid: newLineUid(),
    variantId: input.variantId,
    variantName: input.variantName,
    qty: input.qty,
    note: input.note ?? ""
  };
}

/** Semua baris editor milik satu varian (bisa >1 bila catatannya berbeda). */
export function linesForVariant(
  lines: readonly EditorLine[],
  variantName: string
): EditorLine[] {
  return lines.filter((l) => l.variantName === variantName);
}

/**
 * Total pcs satu varian di daftar — JUMLAH semua barisnya, bukan qty baris
 * pertama. Hint "sudah ada N pcs" di picker per-grup: dengan kembar bercatatan
 * beda (3 "Supi" + 2 "Rina"), membaca baris pertama saja menulis "3 pcs"
 * padahal pesanan itu memegang 5.
 */
export function variantQtyInLines(
  lines: readonly { variantName: string; qty: number }[],
  variantName: string
): number {
  let total = 0;
  for (const l of lines) if (l.variantName === variantName) total += l.qty;
  return total;
}

/**
 * Apa yang AKAN terjadi bila varian+catatan ini ditambahkan — dihitung SEBELUM
 * admin menekan tombol, supaya label tombolnya bisa menyatakan akibatnya
 * ("Gabung ke …" vs "Tambah baris baru"). Tombol yang tidak memberi tahu
 * akibatnya persis yang membuat catatan lama tertimpa tanpa disadari.
 */
export type AddLinePlan =
  | { kind: "merge"; uid: string; note: string; nextQty: number }
  | { kind: "new"; note: string };

export function planAddLine(
  lines: readonly EditorLine[],
  input: { variantName: string; note?: string | null; qty: number }
): AddLinePlan {
  const note = normalizeNote(input.note);
  const identity = lineIdentity(input.variantName, note);
  const existing = lines.find(
    (l) => lineIdentity(l.variantName, l.note) === identity
  );
  return existing
    ? { kind: "merge", uid: existing.uid, note, nextQty: existing.qty + input.qty }
    : { kind: "new", note };
}

/**
 * Terapkan rencana di atas: catatan SAMA → qty dijumlah pada baris itu;
 * catatan BEDA → baris baru. Tidak pernah menimpa catatan baris yang sudah ada.
 */
export function applyAddLine(
  lines: readonly EditorLine[],
  input: {
    variantId: string | null;
    variantName: string;
    note?: string | null;
    qty: number;
  }
): EditorLine[] {
  const plan = planAddLine(lines, input);
  if (plan.kind === "merge") {
    return lines.map((l) =>
      l.uid === plan.uid ? { ...l, qty: plan.nextQty } : l
    );
  }
  return [
    ...lines,
    makeEditorLine({
      variantId: input.variantId,
      variantName: input.variantName,
      qty: input.qty,
      note: plan.note
    })
  ];
}

/** Buang SEMUA baris varian ini (toggle-off di pemilih varian chip datar). */
export function removeVariantLines(
  lines: readonly EditorLine[],
  variantName: string
): EditorLine[] {
  return lines.filter((l) => l.variantName !== variantName);
}

/**
 * Ubah qty / catatan SATU baris. Sasarannya `uid`, BUKAN nama varian: menyasar
 * nama varian ikut mengubah kembaran bercatatan lain — mengetik catatan satu
 * baris menimpa catatan kembarannya.
 */
export function updateEditorLine(
  lines: readonly EditorLine[],
  uid: string,
  patch: Partial<Pick<EditorLine, "qty" | "note">>
): EditorLine[] {
  return lines.map((l) => (l.uid === uid ? { ...l, ...patch } : l));
}

/** Hapus SATU baris; kembarannya yang bercatatan lain tetap. */
export function removeEditorLine(
  lines: readonly EditorLine[],
  uid: string
): EditorLine[] {
  return lines.filter((l) => l.uid !== uid);
}

/**
 * Baris editor dari baris pesanan yang AKAN ditulis ulang (Edit Variasi
 * keranjang admin): baris SEIDENTITAS (varian + catatan) digabung, qty
 * dijumlah; baris bercatatan beda tetap sendiri-sendiri. Urutan kemunculan
 * pertama dipertahankan.
 */
export function editorLinesFromRows(
  rows: readonly {
    variantId: string | null;
    variantName: string;
    qty: number;
    note?: string | null;
  }[]
): EditorLine[] {
  const out: EditorLine[] = [];
  const byIdentity = new Map<string, EditorLine>();
  for (const row of rows) {
    const key = lineIdentity(row.variantName, row.note);
    const prev = byIdentity.get(key);
    if (prev) {
      prev.qty += row.qty;
      if (!prev.variantId && row.variantId) prev.variantId = row.variantId;
      continue;
    }
    const line = makeEditorLine({
      variantId: row.variantId,
      variantName: row.variantName,
      qty: row.qty,
      note: normalizeNote(row.note)
    });
    byIdentity.set(key, line);
    out.push(line);
  }
  return out;
}

// ─── Muat pesanan yang sudah ada ke dalam editor ─────────────────────────────

/** Satu baris `order_items` mentah yang relevan untuk editor. */
export type OrderItemRow = {
  orderId: string;
  variantId: string | null;
  variantName: string;
  qty: number;
  note: string | null;
  /** `order_items.item_status` — selain 'Menunggu' berarti terkunci. */
  itemStatus: string | null;
  /** `order_items.keep_status` — non-null berarti terkunci; 'cancelled' dibuang. */
  keepStatus: string | null;
  /**
   * `order_items.paid_qty` (bughunt D4-3). Dipakai HANYA untuk mencerminkan
   * guard nota server di layar; tidak memengaruhi penggabungan baris.
   */
  paidQty?: number;
  /** Baris ini duduk di nota aktif (pending_acc/open/partial) — idem. */
  inActiveInvoice?: boolean;
  /**
   * Peran hadiah pembelian (`order_items.gift_role`, mig 8680). Baris
   * `'hadiah'` tak pernah jadi baris editor (`aggregateOrderItemsForEdit`).
   */
  giftRole?: string | null;
  /** Pembelian berhadiah baris ini (`order_items.order_gift_id`). */
  orderGiftId?: string | null;
  /** Nama aturan pembeliannya (snapshot `order_gifts.rule_name`) — label keterangan. */
  giftRuleName?: string | null;
};

/** Baris hadiah pembelian di editor: keterangan saja, tak pernah ditulis ulang. */
export type AggregatedGiftItem = {
  variantName: string;
  qty: number;
  ruleName: string | null;
};

export type AggregatedEditItem = {
  variantId: string | null;
  variantName: string;
  qty: number;
  note: string | null;
  /** true = sudah di Produksi / keep → read-only di editor. */
  locked: boolean;
};

/**
 * Cermin klien guard nota `admin_edit_closed_po_order` (bughunt D4-3), dihitung
 * dari baris MENTAH — bukan dari hasil agregasi, karena penggabungan per
 * (varian+catatan) menghapus justru kolom yang menentukan: `item_status`.
 *
 * Predikatnya sengaja disalin satu-per-satu dari SQL-nya:
 *   item_status='Menunggu' AND keep_status IS NULL AND (paid_qty>0 OR nota aktif)
 * Kolom uang yang ABSEN (`undefined`) dibaca sebagai 0/false — pemanggil yang
 * tak menanyakannya memang tak punya jawabannya, dan menebak "pasti terkunci"
 * akan mengunci layar untuk pesanan yang server sendiri terima.
 */
export function closedPoEditLockedRows(
  rows: readonly OrderItemRow[]
): OrderItemRow[] {
  return rows.filter(
    (r) =>
      r.itemStatus === "Menunggu" &&
      r.keepStatus == null &&
      ((r.paidQty ?? 0) > 0 || r.inActiveInvoice === true)
  );
}

/**
 * Gabungkan item dari SEMUA order aktif satu pembeli untuk satu produk.
 *
 * Satu pembeli bisa punya >1 order aktif (checkout ganda), dan editor menulis
 * hasil gabungannya ke order tertua — `extraOrderIds` adalah order duplikat
 * yang perlu dikosongkan saat simpan.
 *
 * Kunci penggabungan: varian + CATATAN. Baris yang identik luar-dalam dijumlah
 * (itu memang duplikat checkout), baris bercatatan berbeda tetap sendiri-
 * sendiri. Mengunci pada nama varian saja — seperti versi lama — meruntuhkan
 * 11 baris "Black" bercatatan campur jadi satu baris bercatatan pertama, lalu
 * Simpan menulis ulang hasil runtuh itu dan catatan yang kalah hilang permanen.
 */
export function aggregateOrderItemsForEdit(
  rows: readonly OrderItemRow[],
  primaryOrderId: string
): { items: AggregatedEditItem[]; extraOrderIds: string[]; gifts: AggregatedGiftItem[] } {
  type Agg = Omit<AggregatedEditItem, "locked">;
  const editableMap = new Map<string, Agg>();
  const lockedMap = new Map<string, Agg>();
  const giftMap = new Map<string, AggregatedGiftItem>();
  const extraOrderIds = new Set<string>();

  for (const row of rows) {
    if (row.keepStatus === "cancelled") continue;
    // Baris HADIAH pembelian (mig 8680) bukan baris editor: server tak pernah
    // menghapusnya saat menulis ulang, jadi mengirimnya lagi melahirkan pcs
    // kembar sebagai barang BERBAYAR (bug 2026-10-29, RENCANA §17.4). Hadiah
    // ikut jumlah barang syaratnya — tampil sebagai keterangan saja.
    if (row.giftRole === "hadiah") {
      const ruleName = (row.giftRuleName ?? "").trim() || null;
      const key = `${ruleName ?? ""}\u0000${row.variantName}`;
      const prev = giftMap.get(key);
      if (prev) prev.qty += row.qty;
      else giftMap.set(key, { variantName: row.variantName, qty: row.qty, ruleName });
      continue;
    }
    const locked = row.itemStatus !== "Menunggu" || row.keepStatus != null;
    const map = locked ? lockedMap : editableMap;
    const key = lineIdentity(row.variantName, row.note);
    const prev = map.get(key);
    if (prev) {
      prev.qty += row.qty;
      if (!prev.variantId && row.variantId) prev.variantId = row.variantId;
    } else {
      map.set(key, {
        variantId: row.variantId,
        variantName: row.variantName,
        qty: row.qty,
        note: row.note
      });
    }
    if (!locked && row.orderId !== primaryOrderId) extraOrderIds.add(row.orderId);
  }

  return {
    items: [
      ...[...editableMap.values()].map((a) => ({ ...a, locked: false })),
      ...[...lockedMap.values()].map((a) => ({ ...a, locked: true }))
    ],
    extraOrderIds: [...extraOrderIds],
    gifts: [...giftMap.values()]
  };
}
